import {
    AfterViewInit,
    ApplicationRef,
    ComponentFactoryResolver,
    ComponentRef,
    Directive,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Injector,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import {ComponentPortal, DomPortalOutlet} from '@angular/cdk/portal';
import {MatOptgroup, MatPseudoCheckbox} from '@angular/material/core';
import {Subscription} from 'rxjs';
import {AppSelectElementDirective, AppSelectElementInterface, AppSelectOptionElementInterface} from '@component/form/input/select/select-element.directive';

export enum AppSelectSelectionState {
    None,
    Some,
    All,
}

@Directive({
    selector: '[appSelectGroup]',
})
export class AppSelectGroupElementDirective implements OnInit, AfterViewInit, OnDestroy, AppSelectElementInterface {
    readonly #subscriptions: Subscription[] = [];

    readonly #matOptGroup: MatOptgroup;

    readonly #select: AppSelectElementDirective;

    readonly #appRef: ApplicationRef;

    readonly #injector: Injector;

    readonly #elementRef: ElementRef;

    readonly #factoryResolver: ComponentFactoryResolver;

    readonly #optionElements: AppSelectOptionElementInterface[] = [];

    #state: AppSelectSelectionState = AppSelectSelectionState.None;

    #checkboxComponentRef!: ComponentRef<MatPseudoCheckbox>;

    #filtered: boolean = false;

    @HostBinding('class')
    readonly class: string = 'mat-optgroup--selectable';

    @HostBinding('class.mat-optgroup--some-selected')
    hasSomeSelected: boolean = false;

    @HostBinding('class.mat-optgroup--hidden')
    hidden: boolean = false;

    @Output()
    groupTitleClick: EventEmitter<number> = new EventEmitter<number>();

    constructor(
        matOptGroup: MatOptgroup,
        select: AppSelectElementDirective,
        appRef: ApplicationRef,
        injector: Injector,
        elementRef: ElementRef,
        factoryResolver: ComponentFactoryResolver,
    ) {
        this.#matOptGroup = matOptGroup;
        this.#select = select;
        this.#appRef = appRef;
        this.#injector = injector;
        this.#elementRef = elementRef;
        this.#factoryResolver = factoryResolver;
    }

    ngOnInit(): void {
        const spanElement: HTMLSpanElement | null = (this.#elementRef.nativeElement as HTMLElement).querySelector('span');

        if (null === spanElement) {
            throw Error('Unable to create portal outlet, missing span element');
        }

        const portal = new ComponentPortal(MatPseudoCheckbox);
        const portalOutlet = new DomPortalOutlet(spanElement, this.#factoryResolver, this.#appRef, this.#injector);

        this.#checkboxComponentRef = portalOutlet.attach(portal);
    }

    ngAfterViewInit(): void {
        this.#select.register(this);
    }

    ngOnDestroy(): void {
        this.#subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    }

    register(selectOptionElement: AppSelectOptionElementInterface): void {
        this.#optionElements.push(selectOptionElement);
        this.#subscriptions.push(selectOptionElement.subscribe(() => this.#update()));
    }

    @HostListener('click', ['$event'])
    handleClick(event: Event, i: number): void {
        const target: HTMLElement | null = null !== event.target ? (event.target as HTMLElement) : null;

        if (target && target.closest('.mat-option--exam-assign-student')) {
            return;
        }

        if (![this.#checkboxComponentRef.location.nativeElement].includes(target)) {
            this.groupTitleClick.emit(i);

            return;
        }

        const shouldCheckAll: boolean = [AppSelectSelectionState.Some, AppSelectSelectionState.None].includes(this.#state);

        this.#optionElements.forEach((option: AppSelectOptionElementInterface) => shouldCheckAll ? option.setSelected(true) : option.setSelected(false));
    }

    isSelected(): boolean {
        return this.#state !== AppSelectSelectionState.None;
    }

    getLabel(): string {
        return this.#matOptGroup.label;
    }

    update(): void {
        this.hidden = this.#filtered && this.#optionElements.every((option: AppSelectOptionElementInterface) => option.isFiltered());
    }

    setFiltered(value: boolean): void {
        this.#filtered = value;
    }

    isFiltered(): boolean {
        return this.#filtered;
    }

    #update(): void {
        const selected: number = this.#optionElements.filter((option: AppSelectOptionElementInterface) => option.isSelected()).length;

        if (selected === this.#optionElements.length) {
            this.#state = AppSelectSelectionState.All;
        } else if (0 !== selected) {
            this.#state = AppSelectSelectionState.Some;
        } else {
            this.#state = AppSelectSelectionState.None;
        }

        this.hasSomeSelected = AppSelectSelectionState.Some === this.#state;
        this.#checkboxComponentRef.instance.state = AppSelectSelectionState.None !== this.#state ? 'checked' : 'unchecked';
    }
}
